import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AdminMainContainer from '../../../../layouts/admin/AdminMainContainer'
import useAdminLeaveRequestDetailViewModel from './viewModel'
import { Fragment } from 'react/jsx-runtime'
import LeaveRequestSourceEnumHelper from '../../../../database/enums/helpers/leave-request-source.helper'
import LeaveTypeEnumHelper from '../../../../database/enums/helpers/leave-type.helper '
import AccountTeamEnumHelper from '../../../../database/enums/helpers/account-team.helper'
import ApprovalStatusEnumHelper from '../../../../database/enums/helpers/approval-status.helper'
import { LeaveType } from '../../../../database/enums/LeaveType.enum'
import LeaveTimeEnumHelper from '../../../../database/enums/helpers/leave-time.helper'
import LeaveRequestService from '../../../../services/leave-request'
import { ApprovalStatus } from '../../../../database/enums/ApprovalStatus.enum'
import ApprovalPopup from '../components/ApprovalPopup'

export default function AdminLeaveRequestDetailPage() {
  const viewModel = useAdminLeaveRequestDetailViewModel()

  const renderLoadingState = () => {
    return (
      <Box sx={{ width: '100%', maxWidth: '500px' }}>
        <Card elevation={5}>
          <CardHeader
            title={<Skeleton variant='text' width={260} />}
            subheader={<Skeleton variant='text' width={100} />}
            action={<Skeleton variant='rounded' width={80} />}
          />
          <Divider />
          <CardContent>
            <Skeleton variant='text' width={200} />
            <Skeleton variant='text' width={240} />
            <Skeleton variant='text' width={260} />
          </CardContent>
          <Divider />
          <CardContent>
            <Skeleton variant='rounded' width={'100%'} height={60} />
          </CardContent>
          <Divider />
          <CardContent>
            <Skeleton variant='rounded' width={'100%'} height={30} />
          </CardContent>
        </Card>
      </Box>
    )
  }

  const renderDetailState = () => {
    if (!!viewModel.detailData)
      return (
        <Box sx={{ width: '100%', maxWidth: '500px' }}>
          <Card elevation={5}>
            <CardHeader
              title={LeaveTypeEnumHelper.getLabel(viewModel.detailData.leaveType)}
              subheader={`${LeaveRequestSourceEnumHelper.getLabel(viewModel.detailData.source)} Team - ${AccountTeamEnumHelper.getLabel(viewModel.detailData.accountTeam)}`}
              action={
                <Chip
                  label={ApprovalStatusEnumHelper.getLabel(viewModel.detailData.approvalStatus)}
                  color={ApprovalStatusEnumHelper.getColor(viewModel.detailData.approvalStatus)}
                />
              }
            />
            <Divider />
            <CardContent>
              {viewModel.detailData.leaveType === LeaveType.OTHER ? (
                <Chip color='info' label={viewModel.detailData.otherLeaveType} sx={{ mb: 2, width: '100%' }} />
              ) : null}
              <Typography fontSize={22} fontWeight={500}>
                {viewModel.detailData.name}
              </Typography>
              <Typography>
                {'as '}
                <Typography component={'span'} fontWeight={600}>
                  {viewModel.detailData.position}
                </Typography>
              </Typography>
              <Stack direction={'row'} gap={1} flexWrap={'wrap'} sx={{ mt: 1 }}>
                <Chip
                  variant='outlined'
                  label={`Time: ${LeaveTimeEnumHelper.getLabel(viewModel.detailData.leaveTime)}`}
                />
                <Chip
                  variant='outlined'
                  label={`Date: ${viewModel.getDateValue(viewModel.detailData.startDate, viewModel.detailData.endDate)}`}
                />
              </Stack>
            </CardContent>
            <Divider />
            <CardContent>
              <TextField
                value={viewModel.detailData.reason ?? '-'}
                fullWidth
                variant='outlined'
                label='Reason'
                multiline
              />
            </CardContent>
            {!!viewModel.detailData.supportingDocumentRef && !!viewModel.detailData.supportingDocumentUrl ? (
              <Fragment>
                <Divider />
                <CardContent>
                  <a
                    href={viewModel.detailData.supportingDocumentUrl}
                    download={LeaveRequestService.getFileNameFromRef(viewModel.detailData.supportingDocumentRef)}
                    target='_blank'
                    rel='noreferrer'
                  >
                    <Button variant='contained' fullWidth>
                      Download file attachment
                    </Button>
                  </a>
                </CardContent>
              </Fragment>
            ) : null}
          </Card>
          {viewModel.detailData.approvalStatus === ApprovalStatus.UNRESPONDED ? (
            <Stack mt={2} direction={'row'} alignItems={'center'} justifyContent={'end'} gap={2}>
              <Button
                variant='contained'
                color={viewModel.canApproval ? 'success' : 'inherit'}
                onClick={viewModel.onApproveClick}
              >
                Approve
              </Button>
              <Button
                variant='contained'
                color={viewModel.canApproval ? 'error' : 'inherit'}
                onClick={viewModel.onRejectClick}
              >
                Reject
              </Button>
            </Stack>
          ) : null}
        </Box>
      )
    // ! NOTES: This will never happen
    return null
  }

  const renderErrorState = () => {
    return (
      <Alert severity='error' variant='filled'>
        <AlertTitle>Error!</AlertTitle>
        Document not found!
      </Alert>
    )
  }

  return (
    <AdminMainContainer title='Leave Request Detail' backButton={{ show: true }}>
      {viewModel.dataFetchStatus.isLoading
        ? renderLoadingState()
        : viewModel.dataFetchStatus.isSuccess && !!viewModel.detailData
          ? renderDetailState()
          : viewModel.dataFetchStatus.isError
            ? renderErrorState()
            : null}

      <ApprovalPopup onConfirm={viewModel.onApprovalPopupConfirm} />
    </AdminMainContainer>
  )
}
